import {memo, useMemo} from 'react'
import {Stack} from '@primer/react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ForYouItem} from '../../for-you/shared/ForYouItem'
import {ForYouLeadingIcon} from '../../for-you/shared/ForYouLeadingIcon'
import type {ReviewSummary} from '../../../../page-data/payloads/review-summaries'
import {getIssueCommentAuthor} from '../util/comment-identity'
import {ReviewDescription} from './ReviewDescription'
import {ReviewTitle} from './ReviewTitle'
import {ReviewContent} from './ReviewContent'
import {REVIEW_STATE_DISPLAY} from '../util/review-state-display'
import type {ReviewThread} from '../data/collect-review-comments'
import {OverviewThread} from '../thread-comments/OverviewThread'

export const ReviewComment = memo(function ForYouReviewCommentItemComponent({
  reviewSummary,
  threads,
}: {
  reviewSummary: ReviewSummary
  threads: ReviewThread[]
}) {
  const {icon, variant, text} = REVIEW_STATE_DISPLAY[reviewSummary.state]
  const viewerLogin = getCurrentUserLogin()
  // Derive the aria label from the same identity the title renders so the accessible name matches the visible label
  const authorIdentity = getIssueCommentAuthor(reviewSummary.authorLogin, viewerLogin)

  const commentsCount = useMemo(
    () => threads.reduce((count, thread) => count + thread.relevantCommentIds.length, 0),
    [threads],
  )

  return (
    // Fixme: migrate off ForYouItem to new design
    <ForYouItem
      key={reviewSummary.id}
      title={
        <ReviewTitle reviewSummary={reviewSummary} commentsCount={commentsCount} authorIdentity={authorIdentity} />
      }
      ariaLabel={`${authorIdentity.login} ${text}`}
      itemName={`review-comment--${text}`}
      leadingVisual={<ForYouLeadingIcon icon={icon} variant={variant} />}
      description={<ReviewDescription reviewSummary={reviewSummary} />}
      padding="none"
    >
      {reviewSummary.body.trim().length > 0 && <ReviewContent reviewSummary={reviewSummary} />}

      {threads.length > 0 && (
        <Stack aria-label="Comments" direction="vertical" gap="normal" padding="normal" as="ul">
          {threads.map(reviewThread => (
            <OverviewThread key={reviewThread.thread.id} thread={reviewThread.thread} />
          ))}
        </Stack>
      )}
    </ForYouItem>
  )
})
